import { useLayoutEffect, useRef } from 'react';

// Scale the complete card and queue together rather than clipping actions below the fold.
export function useArenaFit<T extends HTMLElement = HTMLElement>(
  hasCard: boolean,
  hasFeed = false,
  explore = false,
) {
  const screen = useRef<T>(null);
  useLayoutEffect(() => {
    const node = screen.current;
    if (
      !node ||
      (!explore && !node.parentElement?.classList.contains('has-arena'))
    )
      return;
    const chrome = explore
      ? Array.from(
          node
            .closest('.arena-app')
            ?.querySelectorAll<HTMLElement>(':scope > .top-bar') ?? [],
        )
      : [];
    const stage = node.querySelector<HTMLElement>('.arena-stage');
    const rails = Array.from(node.querySelectorAll<HTMLElement>('.token-rail'));
    const parts = stage ? [stage, ...rails] : rails;
    const sideRails = window.matchMedia(
      explore ? '(min-width: 1181px)' : '(min-width: 761px)',
    );
    let resizeTimer = 0;
    const settleResize = () => {
      node.dataset.resizing = 'true';
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        delete node.dataset.resizing;
      }, 120);
    };
    const fit = () => {
      if (explore) {
        // Align with Arena below the shared header; diagnostics are fixed overlays.
        node.style.top = sideRails.matches
          ? Math.max(
              0,
              ...chrome.map((part) => part.getBoundingClientRect().bottom),
            ) + 'px'
          : '';
      }
      const style = getComputedStyle(node);
      const available =
        node.clientHeight -
        parseFloat(style.paddingTop) -
        parseFloat(style.paddingBottom);
      const footerHeight =
        node
          .closest?.('.arena-app')
          ?.querySelector('.app-footer')
          ?.getBoundingClientRect().height ?? 0;
      for (const part of parts) {
        // Sidebars fit their fixed viewport frame, never the central card/empty state.
        const partAvailable =
          part === stage
            ? available
            : window.innerHeight - 84 - 32 - footerHeight;
        const partStyle = getComputedStyle(part);
        const headingSpace =
          parseFloat(partStyle.getPropertyValue('--rail-heading-space')) || 0;
        // A labeled rail is anchored to its reserved slots, not hover previews
        // or the current item count. Both sides need room around its center.
        const natural = headingSpace
          ? parseFloat(partStyle.getPropertyValue('--rail-height')) +
            2 * headingSpace
          : Math.max(part.scrollHeight, part.offsetHeight);
        const scale =
          sideRails.matches &&
          partAvailable > 0 &&
          natural > 0 &&
          (part !== stage || hasCard)
            ? Math.min(1, partAvailable / natural)
            : 1;
        const zoom = String(Math.floor(scale * 1000) / 1000);
        if (part.style.zoom !== zoom) {
          // CSS zoom can restart a transform transition even when the coin index is unchanged.
          if (part === stage) settleResize();
          part.style.zoom = zoom;
        }
      }
    };
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    observer.observe(node);
    for (const part of chrome) observer.observe(part);
    for (const part of parts) observer.observe(part);
    const resize = () => {
      settleResize();
      fit();
    };
    window.addEventListener('resize', resize);
    sideRails.addEventListener('change', resize);
    fit();
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
      sideRails.removeEventListener('change', resize);
      window.clearTimeout(resizeTimer);
      delete node.dataset.resizing;
      if (explore) node.style.removeProperty('top');
      for (const part of parts) part.style.removeProperty('zoom');
    };
  }, [hasCard, hasFeed, explore]);
  return screen;
}
